import { keyOf } from "@p0security/shared/graph/graph";
import { ConnectedNode } from "@p0security/shared/graph/types";
import { NodeHighlight } from "components/Assessment/components/GraphVisualization";
import { uniqBy, values } from "lodash";
import { ReactNode } from "react";

import { DiscoverLinearCollapse } from "./types";

export type Collapsed<G extends object> = {
  _collapsed: {
    nodes: (ConnectedNode<G, keyof G> & NodeHighlight)[];
    title?: ReactNode;
    type: "like" | "path";
  };
};
export type CollapsedNode<G extends object> = ConnectedNode<
  G & Collapsed<G>,
  keyof (G & Collapsed<G>)
>;
export type GraphEdge<N> = readonly [N, N];
export type CollapsibleNode<G extends object> = {
  key: string;
  type: "_collapsed";
  data: Collapsed<G>["_collapsed"];
};
const isCollapsibleNode = <G extends object>(
  node: CollapsibleNode<G> | ConnectedNode<G, keyof G>
): node is CollapsibleNode<G> => node.type === "_collapsed";

const NULL_SENTINEL = "";

/** Collapses linear paths of a certain length, that start with a matching predicate */
export const collapseLinearPath = <G extends object>(
  edges: Readonly<GraphEdge<CollapsedNode<G> & NodeHighlight>[]>,
  collapse: DiscoverLinearCollapse<G>
): Readonly<GraphEdge<CollapsedNode<G>>[]> => {
  const { predicate, length, title } = collapse;

  // Mapping of all nodes on the displayed subgraph to their edges
  const childEdges = new Map<
    string,
    GraphEdge<CollapsedNode<G> & NodeHighlight>[]
  >();
  const parentEdges = new Map<string, GraphEdge<CollapsedNode<G>>[]>();
  for (const edge of edges) {
    const [parent, child] = edge;
    const parentKey = keyOf(parent);
    childEdges.set(parentKey, [...(childEdges.get(parentKey) ?? []), edge]);
    const childKey = keyOf(child);
    parentEdges.set(childKey, [...(parentEdges.get(childKey) ?? []), edge]);
  }

  // Nodes with these keys will be removed from output
  const remove = new Set<string>();
  // Nodes with these keys will be replaced in output graph
  const replace = new Map<string, CollapsedNode<G>>();

  // Pick out linear sub-paths of graph and mark them for replacement
  for (const edge of edges) {
    if (!predicate(edge[0])) continue;
    let isUnaryPath = true;
    const firstKey = keyOf(edge[0]);
    const path: CollapsedNode<G>[] = [edge[0]];
    let current = childEdges.get(firstKey);
    for (let ix = 0; ix < length - 1; ix++) {
      // If this is not a linear path, mark it as invalid
      if (
        // Previous edge must have only one child
        current?.length !== 1 ||
        // Child can not be multiple nodes
        current[0][1].type === "_collapsed" ||
        // Child can not be highlighted
        current[0][1].highlight ||
        // Child can not have multiple parents
        parentEdges.get(keyOf(current[0][1]))?.length !== 1
      ) {
        isUnaryPath = false;
        break;
      }
      // Otherwise continue to process the path
      const [[_, child]] = current;
      current = childEdges.get(keyOf(child));
      path.push(child);
    }
    if (!isUnaryPath) continue;
    const start = path[0];
    const end = path[length - 1];
    if (!end) continue;
    const node = {
      type: "_collapsed" as const,
      key: path[0].key,
      data: { type: "path", nodes: path, title },
    } as CollapsedNode<G>;
    // Replace graph edges:
    // Remove middle edges of path
    for (const inner of path.slice(1, -1)) {
      remove.add(keyOf(inner));
    }
    // Replace parent edges
    replace.set(keyOf(end), node);
    // Replace child edges
    replace.set(keyOf(start), node);
  }

  // Perform the path node replacement
  const output: GraphEdge<CollapsedNode<G>>[] = [];
  for (const [parent, child] of edges) {
    const parentKey = keyOf(parent);
    const childKey = keyOf(child);
    if (remove.has(parentKey) || remove.has(childKey)) continue;
    output.push([
      replace.get(parentKey) ?? (parent as CollapsedNode<G>),
      replace.get(childKey) ?? (child as CollapsedNode<G>),
    ]);
  }

  return output;
};

/** Collapses groups of nodes with identical singluar parent and child nodes
 *
 * Terminal nodes are also collapsed, as long as the nodes share either one singular
 * parent or one singular child.
 */
export const collapseLikeType = <G extends object>(
  edges: Readonly<GraphEdge<ConnectedNode<G, keyof G>>[]>
): Readonly<GraphEdge<CollapsedNode<G>>[]> => {
  const nodes: Record<
    string,
    {
      value: ConnectedNode<G, keyof G>;
      parents: ConnectedNode<G, keyof G>[];
      children: ConnectedNode<G, keyof G>[];
    }
  > = {};
  for (const [parent, child] of edges) {
    const parentKey = keyOf(parent);
    const childKey = keyOf(child);
    nodes[parentKey] ||= { parents: [], children: [], value: parent };
    nodes[parentKey].children.push(child);
    nodes[childKey] ||= { parents: [], children: [], value: child };
    nodes[childKey].parents.push(parent);
  }
  const collapsable: Record<
    string,
    {
      key: string;
      parent: string;
      child: string;
      nodes: ConnectedNode<G, keyof G>[];
    }
  > = {};
  for (const node of values(nodes)) {
    node.parents = uniqBy<ConnectedNode<G, keyof G>>(node.parents, keyOf);
    node.children = uniqBy<ConnectedNode<G, keyof G>>(node.children, keyOf);
    if (node.parents.length <= 1 && node.children.length <= 1) {
      const parent = node.parents.at(0);
      const child = node.children.at(0);
      const parentKey = parent ? keyOf(parent) : NULL_SENTINEL;
      const childKey = child ? keyOf(child) : NULL_SENTINEL;
      // double colon b/c some keys have single colons
      const key = `${String(node.value.type)}::${parentKey}::${childKey}`;
      collapsable[key] ||= {
        key,
        nodes: [],
        parent: parentKey,
        child: childKey,
      };
      collapsable[key].nodes.push(node.value);
    }
  }
  const collapsed: Record<string, CollapsibleNode<G>> = {};
  for (const { key, nodes } of values(collapsable)) {
    if (nodes.length > 1) {
      for (const node of nodes) {
        collapsed[keyOf(node)] = {
          type: "_collapsed",
          key,
          data: { nodes, type: "like" },
        };
      }
    }
  }
  const collapsedEdges = edges.map(
    ([parent, child]) =>
      [
        collapsed[keyOf(parent)] ?? parent,
        collapsed[keyOf(child)] ?? child,
      ] as any as GraphEdge<
        ConnectedNode<G & Collapsed<G>, keyof (G & Collapsed<G>)>
      >
  );
  return uniqBy(
    collapsedEdges,
    ([parent, child]) =>
      // triple colon b/c keys can already have double colons
      `${isCollapsibleNode(parent) ? parent.key : keyOf(parent)}:::${
        isCollapsibleNode(child) ? child.key : keyOf(child)
      }`
  );
};
